import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ArrowRight, Search, MapPin } from "lucide-react";
import { api } from "../lib/api";
import Seo from "../components/Seo";

// Fix default Leaflet marker icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Custom branded pin (red, matches site accent)
const redPin = new L.DivIcon({
  className: "gens-map-pin",
  html: `<div style="
    width:30px;height:38px;position:relative;
    filter: drop-shadow(0 2px 4px rgba(0,0,0,0.35));
  ">
    <svg width="30" height="38" viewBox="0 0 30 38" xmlns="http://www.w3.org/2000/svg">
      <path d="M15 0C6.7 0 0 6.7 0 15c0 11.3 15 23 15 23s15-11.7 15-23C30 6.7 23.3 0 15 0z" fill="#E63946"/>
      <circle cx="15" cy="15" r="6" fill="#fff"/>
    </svg>
  </div>`,
  iconSize: [30, 38],
  iconAnchor: [15, 38],
  popupAnchor: [0, -34],
});

// Fly-to control invoked when a side-panel item is clicked
function FlyToCity({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target?.lat && target?.lng) {
      map.flyTo([target.lat, target.lng], 13, { duration: 1.2 });
    }
  }, [target, map]);
  return null;
}

const HOUSTON_CENTER = [29.7604, -95.3698]; // Downtown Houston
const fmt = (n) => `$${Number(n).toLocaleString()}`;

export default function MapSearch() {
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const markerRefs = useRef({});

  useEffect(() => {
    api.get("/neighborhoods").then((r) => setNeighborhoods(r.data || [])).catch(() => setNeighborhoods([]));
  }, []);

  const withCoords = useMemo(
    () => neighborhoods.filter((n) => n.lat && n.lng),
    [neighborhoods]
  );

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return withCoords;
    return withCoords.filter(
      (n) =>
        n.name.toLowerCase().includes(q) ||
        n.tagline?.toLowerCase().includes(q) ||
        n.highlights?.some((h) => h.toLowerCase().includes(q))
    );
  }, [withCoords, filter]);

  const handleSidebarClick = (n) => {
    setSelected({ ...n, _t: Date.now() }); // _t forces effect to retrigger on same item
    const m = markerRefs.current[n.slug];
    if (m) {
      setTimeout(() => m.openPopup(), 800);
    }
  };

  return (
    <div className="bg-stone-50 min-h-screen" data-testid="map-search-page">
      <Seo
        title="Houston Neighborhood Map"
        description="Explore Houston-area neighborhoods on an interactive map — River Oaks, The Heights, Memorial, Montrose, Midtown, West U, Katy, The Woodlands, Sugar Land, Cypress, and more."
        path="/map-search"
      />

      {/* Hero strip */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#E63946] mb-3">
          Houston · Map Search
        </div>
        <h1 className="font-display text-4xl lg:text-6xl font-light tracking-tighter text-[#1A1A1A] max-w-3xl">
          Explore Houston, <em className="not-italic text-[#E63946]">on the map</em>.
        </h1>
        <p className="text-[#555] mt-5 max-w-2xl text-lg">
          Click any pin to see neighborhood highlights, median prices, and jump into our detailed area guide.
        </p>
      </section>

      {/* Map + Sidebar */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Sidebar */}
          <aside className="lg:col-span-4 order-2 lg:order-1">
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden flex flex-col h-[640px]">
              <div className="p-4 border-b border-gray-100 bg-stone-50">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Filter neighborhoods…"
                    className="w-full pl-9 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#E63946]"
                    data-testid="map-search-filter"
                  />
                </div>
                <div className="mt-3 text-[10px] uppercase tracking-[0.18em] text-gray-500">
                  {visible.length} {visible.length === 1 ? "Neighborhood" : "Neighborhoods"}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto divide-y divide-gray-100" data-testid="map-search-list">
                {visible.map((n) => (
                  <button
                    key={n.slug}
                    onClick={() => handleSidebarClick(n)}
                    className={`w-full text-left px-5 py-4 hover:bg-stone-50 transition-colors ${
                      selected?.slug === n.slug ? "bg-stone-50" : ""
                    }`}
                    data-testid={`map-search-list-item-${n.slug}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-display text-lg font-semibold text-[#1A1A1A]">{n.name}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{n.tagline}</div>
                      </div>
                      <MapPin className="w-4 h-4 text-[#E63946] mt-1 shrink-0" />
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      Median <span className="text-[#1A1A1A] font-medium">{fmt(n.median_price)}</span>
                      <span className="mx-2 text-gray-300">·</span>
                      <span className="text-[#1A1A1A] font-medium">${n.avg_sqft_price}</span>/sqft
                    </div>
                  </button>
                ))}
                {visible.length === 0 && (
                  <div className="text-center text-gray-500 py-12 text-sm">
                    No neighborhoods match &quot;{filter}&quot;.
                  </div>
                )}
              </div>
            </div>
          </aside>

          {/* Map */}
          <div className="lg:col-span-8 order-1 lg:order-2">
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden h-[640px] relative" data-testid="map-search-map">
              <MapContainer
                center={HOUSTON_CENTER}
                zoom={10}
                scrollWheelZoom
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FlyToCity target={selected} />
                {visible.map((n) => (
                  <Marker
                    key={n.slug}
                    position={[n.lat, n.lng]}
                    icon={redPin}
                    ref={(ref) => {
                      if (ref) markerRefs.current[n.slug] = ref;
                    }}
                  >
                    <Popup>
                      <div className="w-56" data-testid={`map-search-popup-${n.slug}`}>
                        {n.image && (
                          <img
                            src={n.image}
                            alt={n.name}
                            className="w-full h-28 object-cover rounded-md mb-2"
                          />
                        )}
                        <div className="font-display text-lg font-semibold text-[#1A1A1A] leading-tight">{n.name}</div>
                        <div className="text-[11px] text-gray-500 mt-0.5">{n.tagline}</div>
                        <div className="mt-2 text-xs text-[#444]">
                          Median <strong>{fmt(n.median_price)}</strong> · <strong>${n.avg_sqft_price}</strong>/sqft
                        </div>
                        <Link
                          to={`/neighborhoods/${n.slug}`}
                          className="mt-3 inline-flex items-center gap-1 text-[#E63946] text-sm font-semibold hover:gap-2 transition-all"
                          data-testid={`map-search-popup-link-${n.slug}`}
                        >
                          Explore {n.name} <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-gray-500 mt-3">
              Map data © OpenStreetMap contributors · Coordinates are approximate centroids
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 bg-[#1A1A1A] text-white rounded-2xl p-10 lg:p-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-display text-3xl lg:text-4xl font-light tracking-tighter">
              Found your neighborhood? <em className="not-italic text-[#E63946]">Let&apos;s find your home.</em>
            </h2>
            <p className="text-white/80 mt-4 max-w-xl">
              Browse curated Gens Realty listings or search the full Houston MLS.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <Link
              to="/properties"
              className="bg-[#E63946] hover:bg-[#c9313d] text-white px-7 py-3.5 rounded-md font-semibold text-sm text-center"
              data-testid="map-cta-properties"
            >
              Browse Properties
            </Link>
            <Link
              to="/mls-search"
              className="bg-white/10 hover:bg-white/15 text-white px-7 py-3.5 rounded-md font-semibold text-sm text-center border border-white/20"
              data-testid="map-cta-mls"
            >
              Search the MLS
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
